import React from 'react'
import { createPortal } from 'react-dom'

import { useStatusBannerHost } from '../hooks/useStatusBannerHost'
import { bannerFlexOrder } from '../utils/statusBannerStack'

interface LiveKitAudioResumePromptProps {
  onResume: () => void
}

/**
 * SC-257: non-blocking banner shown when the browser has suspended or blocked remote
 * audio playback — typically the webAudioMix AudioContext being suspended while the Sevv
 * tab is backgrounded (Safari/iOS), or audio elements created while hidden staying muted
 * under autoplay policy. A single click is a user gesture that reliably resumes playback
 * via `room.startAudio()`, so this is the guaranteed recovery path when programmatic
 * resume is blocked.
 *
 * Rendered only while `room.canPlaybackAudio === false`; it auto-hides once playback resumes.
 *
 * Renders through a portal into `StatusBannerViewport`. That column is click-through, so the
 * game canvas stays interactive and only this button takes pointer events — the same guarantee
 * the component's own overlay used to provide, now owned in one place. It keeps the top slot: of
 * the three banners this is the only one the user must click to recover, so the connection and
 * zoom notices sit below it rather than the other way round.
 */
const LiveKitAudioResumePrompt: React.FC<LiveKitAudioResumePromptProps> = ({ onResume }) => {
  const host = useStatusBannerHost()

  if (!host) return null

  return createPortal(
    // The live region goes on this non-interactive wrapper, never on the button. `role="status"`
    // would override the button's implicit ARIA role and assistive tech would stop announcing it
    // as actionable — on the one banner the user MUST click to recover a dead room. The wrapper
    // carries the column slot too; it does no positioning, which is still the column's job.
    <div role="status" aria-live="polite" style={{ order: bannerFlexOrder('audio-resume') }}>
      <button
        type="button"
        onClick={onResume}
        style={{
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 18px',
          borderRadius: 9999,
          border: 'none',
          background: 'var(--sevv-primary, #f97415)',
          color: 'var(--sevv-white, #ffffff)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: 'var(--sevv-shadow-primary, 0 4px 14px 0 rgba(249, 116, 21, 0.3))',
        }}
      >
        <span aria-hidden="true">🔊</span>
        Audio paused - click to resume
      </button>
    </div>,
    host,
  )
}

export default LiveKitAudioResumePrompt
